<template>
  <div
    class="ca-slider-card-skeleton"
    :class="`ca-slider-card-skeleton--${variant}`"
    aria-hidden="true"
  >
    <div class="ca-slider-card-skeleton__info">
      <span
        class="ca-slider-card-skeleton__label gradient-loading"
      ></span>

      <div class="ca-slider-card-skeleton__copy">
        <span
          v-for="line in 4"
          :key="line"
          class="ca-slider-card-skeleton__line gradient-loading"
          :class="`ca-slider-card-skeleton__line--${line}`"
        ></span>
      </div>

      <span
        class="ca-slider-card-skeleton__action ca-slider-card-skeleton__action--desktop gradient-loading"
        :class="`ca-slider-card-skeleton__action--${variant}`"
      ></span>
    </div>

    <div class="ca-slider-card-skeleton__visual">
      <span
        class="ca-slider-card-skeleton__media gradient-loading"
      ></span>

      <div
        v-if="variant === 'simulator'"
        class="ca-slider-card-skeleton__choices"
      >
        <span
          v-for="choice in buttonCount"
          :key="choice"
          class="ca-slider-card-skeleton__choice gradient-loading"
        ></span>
      </div>

      <span
        v-if="variant === 'content'"
        class="ca-slider-card-skeleton__floating-action gradient-loading"
      ></span>
      <span
        v-else
        class="ca-slider-card-skeleton__mobile-cta gradient-loading"
      ></span>
    </div>
  </div>
</template>

<script setup>
defineOptions({
  name: "CaSliderCardSkeleton",
});

defineProps({
  variant: {
    type: String,
    default: "content",
    validator: (value) => ["content", "simulator"].includes(value),
  },
  buttonCount: {
    type: Number,
    default: 6,
  },
});
</script>

<style scoped lang="scss">
.ca-slider-card-skeleton {
  position: absolute;
  inset: var(--size-4);
  top: var(--size-3);
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: var(--size-4);
  pointer-events: none;

  .gradient-loading {
    background-image: linear-gradient(
      90deg,
      var(--ca-color-neutrals-inverted-10, rgba(255, 255, 255, 0.1)) 0%,
      var(--ca-color-neutrals-inverted-10, rgba(255, 255, 255, 0.1)) 25%,
      var(--ca-color-neutrals-inverted-20, rgba(255, 255, 255, 0.2)) 50%,
      var(--ca-color-neutrals-inverted-30, rgba(255, 255, 255, 0.3)) 75%,
      var(--ca-color-neutrals-inverted-10, rgba(255, 255, 255, 0.1)) 100%
    );
  }

  &__info {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--size-4);
    min-height: 0;
    padding: var(--size-4) var(--size-3);
  }

  &__label {
    display: block;
    width: min(148px, 55%);
    height: 38px;
    flex-shrink: 0;
    border-radius: 100px;
  }

  &__copy {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    justify-content: center;
    gap: var(--size-3);
    width: 100%;
    min-height: 0;
  }

  &__line {
    display: block;
    width: 100%;
    height: 16px;
    flex-shrink: 1;
    border-radius: var(--size-3);

    &--2 {
      width: 96%;
    }

    &--3 {
      width: 92%;
    }

    &--4 {
      display: none;
      width: 84%;
    }
  }

  &__action {
    flex-shrink: 0;

    &--desktop {
      display: none;
    }

    &--content {
      width: 48px;
      height: 48px;
      border-radius: 100px;
    }

    &--simulator {
      width: 140px;
      height: 48px;
      border-radius: var(--size-3);
    }
  }

  &__visual {
    position: relative;
    flex: 0 0 360px;
    width: 100%;
    overflow: hidden;
    border-radius: var(--size-4);
  }

  &__media {
    position: absolute;
    inset: 0;
    display: block;
    border-radius: var(--size-4);
  }

  &__choices {
    position: absolute;
    right: var(--size-4);
    bottom: 80px;
    left: var(--size-4);
    z-index: 1;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--size-3);
  }

  &__choice {
    display: block;
    height: 44px;
    border-radius: var(--size-3);
  }

  &__floating-action {
    position: absolute;
    right: var(--size-4);
    bottom: var(--size-4);
    z-index: 1;
    display: block;
    width: 48px;
    height: 48px;
    border-radius: 100px;
  }

  &__mobile-cta {
    position: absolute;
    right: var(--size-4);
    bottom: var(--size-4);
    left: var(--size-4);
    z-index: 1;
    display: block;
    height: 48px;
    border-radius: var(--size-3);
  }

  @include ca-bp("sm") {
    inset: var(--size-4);
    flex-direction: row;
    align-items: flex-start;
    gap: var(--size-5);

    &--content {
      .ca-slider-card-skeleton__info {
        flex: 0 0 332px;
        width: 332px;
      }

      .ca-slider-card-skeleton__visual {
        flex: 0 0 332px;
        width: 332px;
      }
    }

    &--simulator {
      .ca-slider-card-skeleton__info {
        flex: 0 0 286px;
        width: 286px;
      }
    }

    &__info {
      justify-content: center;
      height: 100%;
    }

    &__line {
      height: 28px;

      &--4 {
        display: block;
      }
    }

    &__action--desktop {
      display: block;
    }

    &__visual {
      height: 100%;
      flex: 1 1 0;
      width: auto;
    }

    &__choices {
      bottom: var(--size-4);
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    &__floating-action,
    &__mobile-cta {
      display: none;
    }
  }

  @include ca-bp("md") {
    &--content {
      .ca-slider-card-skeleton__info,
      .ca-slider-card-skeleton__visual {
        flex: 1 1 0;
        width: auto;
      }
    }

    &--simulator {
      .ca-slider-card-skeleton__info {
        flex: 0 0 312px;
        width: 312px;
      }
    }
  }

  @include ca-bp("lg") {
    &--content {
      .ca-slider-card-skeleton__info {
        flex: 0 0 312px;
        width: 312px;
      }
    }
  }
}
</style>